// =====================================================================
// EGL v12 Frontend - SESSION EXTENSION MANAGER
// Purpose: Handle session extension with proactive user interaction
// =====================================================================

import { TokenManager } from './tokenManager';

interface SessionState {
  showExtensionButton: boolean;
  timeUntilExpiry: string;
  isExpired: boolean;
  canExtend: boolean;
}

interface SessionExtensionCallbacks {
  onShowExtensionButton: (timeLeft: string) => void;
  onHideExtensionButton: () => void;
  onSessionExpired: () => void;
  onExtensionSuccess: (newToken: string) => void;
  onExtensionFailed: (error: string) => void;
}

export class SessionManager {
  private static readonly EXTENSION_WARNING_TIME = 5 * 60 * 1000; // 5 minutes before expiry (for easier testing)
  private static readonly CHECK_INTERVAL = 5 * 1000; // Check every 5 seconds for better responsiveness
  private static monitoringInterval: NodeJS.Timeout | null = null;
  private static callbacks: SessionExtensionCallbacks | null = null;
  private static lastWarningTime = 0;

  /**
   * Start session monitoring with extension capabilities
   */
  static startSessionMonitoring(callbacks: SessionExtensionCallbacks): () => void {
    this.callbacks = callbacks;
    
    // Clear any existing monitoring
    if (this.monitoringInterval) {
      clearInterval(this.monitoringInterval);
    }

    this.monitoringInterval = setInterval(() => {
      this.checkSessionStatus();
    }, this.CHECK_INTERVAL);

    console.log('🔍 Session monitoring started with extension capabilities');

    // Return cleanup function
    return () => {
      if (this.monitoringInterval) {
        clearInterval(this.monitoringInterval);
        this.monitoringInterval = null;
      }
      this.callbacks = null;
    };
  }

  /**
   * Check current session status and trigger appropriate callbacks
   */
  private static checkSessionStatus(): void {
    const tokenInfo = TokenManager.getTokenInfo();
    
    if (!tokenInfo || !this.callbacks) {
      console.log('🔍 SessionManager: No token info or callbacks available');
      return;
    }

    console.log('🔍 SessionManager check:', {
      isExpired: tokenInfo.isExpired,
      timeUntilExpiry: tokenInfo.timeUntilExpiry,
      warningThreshold: this.EXTENSION_WARNING_TIME
    });

    // If token is expired, trigger expiry callback
    if (tokenInfo.isExpired) {
      console.log('🔍 Token expired, triggering session expiry');
      console.log('🔍 DEBUGGING: Token expiry details:', {
        tokenExp: tokenInfo.expiresAt,
        currentTime: Date.now(),
        timeDiff: tokenInfo.timeUntilExpiry,
        expiredBy: Math.abs(tokenInfo.timeUntilExpiry)
      });
      // TEMPORARILY DISABLED FOR DEBUGGING - DO NOT LOGOUT IMMEDIATELY
      console.log('🔍 LOGOUT TEMPORARILY DISABLED FOR DEBUGGING');
      // this.callbacks.onSessionExpired();
      return;
    }

    // If within extension warning time, show extension button
    if (tokenInfo.timeUntilExpiry <= this.EXTENSION_WARNING_TIME) {
      const timeLeft = TokenManager.getTimeUntilExpiry();
      
      // Only show warning periodically to avoid spam, but allow more frequent updates for countdown
      const now = Date.now();
      if (now - this.lastWarningTime > 5000) { // 5 seconds between warnings for better countdown updates
        console.log(`🔍 Session expires in ${timeLeft}, showing extension button`);
        console.log('🔍 Triggering onShowExtensionButton callback');
        this.callbacks.onShowExtensionButton(timeLeft);
        this.lastWarningTime = now;
      } else {
        console.log(`🔍 Session warning suppressed (last warning ${Math.round((now - this.lastWarningTime) / 1000)}s ago)`);
      }
    } else {
      // Hide extension button if we're not near expiry
      console.log('🔍 Session not near expiry, hiding extension button');
      this.callbacks.onHideExtensionButton();
    }
  }

  /**
   * Extend session by re-authenticating with current credentials
   */
  static async extendSession(username: string, password: string): Promise<{
    success: boolean;
    token?: string;
    message?: string;
  }> {
    try {
      console.log('🔍 Attempting session extension...');
      
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          username,
          password
        })
      });

      const result = await response.json();

      if (result.success && result.data?.token) {
        // Store new token using both TokenManager and localStorage for consistency
        TokenManager.setToken(result.data.token);
        localStorage.setItem('egl_token', result.data.token); // Ensure SmartRouter compatibility
        
        console.log('✅ Session extended successfully');
        
        if (this.callbacks) {
          this.callbacks.onExtensionSuccess(result.data.token);
        }

        return {
          success: true,
          token: result.data.token,
          message: 'Session extended for another 15 minutes'
        };
      } else {
        console.error('❌ Session extension failed:', result.message);
        
        if (this.callbacks) {
          this.callbacks.onExtensionFailed(result.message || 'Extension failed');
        }

        return {
          success: false,
          message: result.message || 'Failed to extend session'
        };
      }
    } catch (error) {
      console.error('❌ Session extension error:', error);
      
      const errorMessage = error instanceof Error ? error.message : 'Network error';
      
      if (this.callbacks) {
        this.callbacks.onExtensionFailed(errorMessage);
      }

      return {
        success: false,
        message: errorMessage
      };
    }
  }

  /**
   * Get current session state
   */
  static getSessionState(): SessionState {
    const tokenInfo = TokenManager.getTokenInfo();
    
    if (!tokenInfo) {
      return {
        showExtensionButton: false,
        timeUntilExpiry: 'Unknown',
        isExpired: true,
        canExtend: false
      };
    }

    return {
      showExtensionButton: tokenInfo.timeUntilExpiry <= this.EXTENSION_WARNING_TIME && !tokenInfo.isExpired,
      timeUntilExpiry: TokenManager.getTimeUntilExpiry(),
      isExpired: tokenInfo.isExpired,
      canExtend: !tokenInfo.isExpired && tokenInfo.timeUntilExpiry <= this.EXTENSION_WARNING_TIME
    };
  }

  /**
   * Stop session monitoring
   */
  static stopSessionMonitoring(): void {
    if (this.monitoringInterval) {
      clearInterval(this.monitoringInterval);
      this.monitoringInterval = null;
    }
    this.callbacks = null;
    console.log('🔍 Session monitoring stopped');
  }

  /**
   * Reset warning time (useful after successful extension)
   */
  static resetWarningTime(): void {
    this.lastWarningTime = 0;
  }

  /**
   * Reset session monitoring (useful after login/logout)
   */
  static resetSessionMonitoring(): void {
    this.lastWarningTime = 0;
    console.log('🔍 Session monitoring reset');
  }
} 